import {
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  linkedSignal,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { AuthService } from '../../../../../core/services/auth/auth.service';
import { AlertaService } from '../../../../../shared/utils/alerta.service';
import { LucideModule } from '../../../../../shared/modules/lucide.module';
import { AgendamentoResponse, ErroAgendamento, SituacaoAgendamento } from '../../../models/agendamento.model';
import { AgendamentosService, dataEHoraDe, mapearErroAgendamento } from '../../../services/agendamentos.service';
import { dataHoraHistorico, tomDoStatusSolicitacao } from '../../../services/solicitacao-servico-publica.service';
import { TomHistorico, formatarData } from '../../../services/solicitacoes.service';
import { ModalReagendamentoComponent } from './modal-reagendamento/modal-reagendamento.component';

type Aba = 'todos' | 'proximos' | 'realizados' | 'cancelados';

interface FiltroSituacao {
  id: Aba;
  rotulo: string;
  /** A situação que a aba mostra; nulo é "todas" — inclusive Expirado e Arquivado, que não têm aba. */
  situacao: SituacaoAgendamento | null;
}

/** O que a aba mostra no lugar da lista — ou, em `pronto`, a própria lista. */
type EstadoAgendamentos = 'sessao' | 'carregando' | 'falha' | 'pronto';

/** Um evento do histórico, como a linha do tempo do detalhe o mostra. */
interface EventoLinhaDoTempo {
  rotulo: string;
  tom: TomHistorico;
  quando: string;
  descricao: string;
}

const SITUACOES: readonly FiltroSituacao[] = [
  { id: 'todos', rotulo: 'Todos', situacao: null },
  { id: 'proximos', rotulo: 'Próximos', situacao: 'Agendado' },
  { id: 'realizados', rotulo: 'Realizados', situacao: 'Realizado' },
  { id: 'cancelados', rotulo: 'Cancelados', situacao: 'Cancelado' },
];

const ICONE_POR_SITUACAO: Readonly<Record<SituacaoAgendamento, string>> = {
  Agendado: 'calendar-clock',
  Expirado: 'clock-alert',
  Realizado: 'check',
  Cancelado: 'circle-x',
  Arquivado: 'archive',
};

const PRAZO_ENCERRADO = 'O prazo para alterar ou cancelar pelo portal terminou. Procure o órgão responsável.';

/**
 * Os agendamentos de quem está logado, com o detalhe, o reagendamento e o cancelamento.
 *
 * A situação de cada item (`desSituacao`), se ele ainda pode ser cancelado ou reagendado
 * (`bitPodeCancelar`, `bitPodeReagendar`) e até quando (`datLimite*`) vêm prontos da API,
 * calculados com o relógio do servidor: a tela não compara datas nem conhece o prazo.
 *
 * A modalidade é fixa em "Presencial" — não há videoatendimento no domínio.
 */
@Component({
  selector: 'app-meus-agendamentos',
  templateUrl: './meus-agendamentos.component.html',
  styleUrls: ['./meus-agendamentos.component.css'],
  imports: [LucideModule, RouterLink, ModalReagendamentoComponent],
})
export class MeusAgendamentosComponent {
  private readonly agendamentosService = inject(AgendamentosService);
  private readonly alerta = inject(AlertaService);
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly modalDetalhe = viewChild<ElementRef<HTMLDialogElement>>('modalDetalhe');
  private readonly modalReagendamento = viewChild(ModalReagendamentoComponent);

  private readonly usuario = toSignal(this.auth.currentUser, {
    initialValue: this.auth.currentUserValue,
  });

  /**
   * Distingue "sessão encerrada" de "falha": num 401 o `ErrorInterceptor` faz logout, e a aba
   * continua na tela.
   */
  private readonly logado = computed(() => !!this.usuario()?.token);

  readonly situacoes = SITUACOES;

  readonly situacao = signal<Aba>('todos');
  readonly selecionado = signal<AgendamentoResponse | null>(null);

  /** Um cancelamento em curso — do pedido do motivo à resposta: trava o segundo clique. */
  readonly cancelando = signal(false);

  private readonly todos = computed<readonly AgendamentoResponse[]>(
    () => this.agendamentosService.agendamentos() ?? [],
  );

  readonly estado = computed<EstadoAgendamentos>(() => {
    if (!this.logado()) return 'sessao';
    if (this.agendamentosService.falhou()) return 'falha';

    return this.agendamentosService.agendamentos() === null ? 'carregando' : 'pronto';
  });

  /** Os nomes de serviço que aparecem na lista, sem repetição: as opções do filtro "Serviço". */
  readonly servicos = computed<readonly string[]>(() =>
    [...new Set(this.todos().map((item) => item.desNomeServicoSsv))].sort((a, b) => a.localeCompare(b, 'pt-BR')),
  );

  /** Preso às opções: o serviço filtrado que sai da lista relida deixa de filtrar. */
  readonly servico = linkedSignal<readonly string[], string>({
    source: this.servicos,
    computation: (servicos, anterior) => {
      const escolhido = anterior?.value ?? '';

      return servicos.includes(escolhido) ? escolhido : '';
    },
  });

  /** Na ordem da API: do agendamento mais recente para o mais antigo. */
  readonly lista = computed<readonly AgendamentoResponse[]>(() => {
    const situacao = this.situacoes.find((opcao) => opcao.id === this.situacao())?.situacao ?? null;
    const servico = this.servico();

    return this.todos().filter(
      (item) =>
        (!servico || item.desNomeServicoSsv === servico) && (situacao === null || item.desSituacao === situacao),
    );
  });

  readonly total = computed(() => this.lista().length);

  /** Quem nunca agendou vê um vazio próprio, sem filtros: não há o que filtrar. */
  readonly semAgendamentos = computed(() => this.todos().length === 0);

  readonly totalProximos = computed(() => this.todos().filter((item) => item.desSituacao === 'Agendado').length);

  /** O histórico do agendamento aberto, do evento mais recente para o mais antigo. */
  readonly linhaDoTempo = computed<readonly EventoLinhaDoTempo[]>(() =>
    [...(this.selecionado()?.historico ?? [])].reverse().map((evento) => ({
      rotulo: evento.desStatusNovoEvs,
      tom: tomDoStatusSolicitacao(evento.desStatusNovoEvs),
      quando: dataHoraHistorico(evento.datEventoEvs),
      descricao: evento.desMensagemEvs,
    })),
  );

  constructor() {
    this.agendamentosService.carregar();

    // Sessão encerrada com um modal aberto: ele não pode ficar mostrando o agendamento de quem
    // saiu por cima do aviso de sessão.
    effect(() => {
      if (this.logado()) return;

      untracked(() => {
        this.fecharDetalhes();
        this.modalReagendamento()?.fechar();
        this.selecionado.set(null);
      });
    });
  }

  iconeSituacao(situacao: SituacaoAgendamento): string {
    return ICONE_POR_SITUACAO[situacao] ?? 'calendar';
  }

  /** `2026-10-13T09:40:00` → `13/10/2026`. Só texto: a data não passa pelo fuso do navegador. */
  dataDe(item: AgendamentoResponse): string {
    return formatarData(item.datAgendamentoSsv.slice(0, 10));
  }

  /** `2026-10-13T09:40:00` → `09:40`. */
  horaDe(item: AgendamentoResponse): string {
    return item.datAgendamentoSsv.slice(11, 16);
  }

  /** Quem será atendido, com o vínculo — o mesmo rótulo do campo "Cidadão" do agendamento. */
  rotuloCidadao(item: AgendamentoResponse): string {
    return `${item.desNomeAtendido} (${item.bitDependente ? 'Dependente' : 'Titular'})`;
  }

  /**
   * O agendamento ainda vai acontecer, mas o cidadão não pode mais alterá-lo nem cancelá-lo pelo
   * portal: o prazo terminou, e só o órgão age. Os dois `bit` vêm da API — nada de data aqui.
   */
  prazoEncerrado(item: AgendamentoResponse): boolean {
    return item.desSituacao === 'Agendado' && !item.bitPodeCancelar && !item.bitPodeReagendar;
  }

  /**
   * As frases do prazo no detalhe de um agendamento `Agendado` — vazio nas demais situações.
   *
   * Uma frase só quando as duas ações valem até o mesmo instante; com limites diferentes (ou uma
   * ação já encerrada), uma frase para cada ação que ainda vale.
   */
  avisosDePrazo(item: AgendamentoResponse): readonly string[] {
    if (item.desSituacao !== 'Agendado') return [];
    if (this.prazoEncerrado(item)) return [PRAZO_ENCERRADO];

    if (item.bitPodeReagendar && item.bitPodeCancelar && item.datLimiteReagendamento === item.datLimiteCancelamento) {
      return [
        `Você pode alterar ou cancelar este agendamento pelo portal até ${dataEHoraDe(item.datLimiteReagendamento)}.`,
      ];
    }

    const avisos: string[] = [];

    if (item.bitPodeReagendar) {
      avisos.push(`Você pode alterar este agendamento pelo portal até ${dataEHoraDe(item.datLimiteReagendamento)}.`);
    }

    if (item.bitPodeCancelar) {
      avisos.push(`Você pode cancelar este agendamento pelo portal até ${dataEHoraDe(item.datLimiteCancelamento)}.`);
    }

    return avisos;
  }

  filtrarPor(situacao: Aba): void {
    this.situacao.set(situacao);
  }

  aoTrocarServico(evento: Event): void {
    this.servico.set((evento.target as HTMLSelectElement).value);
  }

  tentarNovamente(): void {
    this.agendamentosService.recarregar();
  }

  verDetalhes(agendamento: AgendamentoResponse): void {
    this.selecionado.set(agendamento);
    this.modalDetalhe()?.nativeElement.showModal();
  }

  /** O `close()` nativo devolve o foco ao botão que abriu o detalhe. */
  fecharDetalhes(): void {
    const modal = this.modalDetalhe()?.nativeElement;

    if (modal?.open) modal.close();
  }

  reagendar(agendamento: AgendamentoResponse): void {
    // ⚠️ O detalhe fecha ANTES: dois `<dialog>` modais empilhados deixam o de baixo aberto e
    // inerte, e é para ele que o foco voltaria.
    this.fecharDetalhes();

    this.modalReagendamento()?.abrir(agendamento);
  }

  /** O modal já fechou e a lista já está sendo relida pelo serviço: só falta o aviso. */
  aoReagendar(reagendado: AgendamentoResponse): void {
    void this.alerta.sucesso({
      titulo: 'Agendamento alterado',
      html: `O atendimento do protocolo <b>${reagendado.desProtocoloSsv}</b> passou para
             <b>${this.dataDe(reagendado)}</b> às <b>${this.horaDe(reagendado)}</b>.`,
    });
  }

  /**
   * O reagendamento foi recusado de vez (o prazo terminou, o agendamento mudou, o posto fechou):
   * o modal já fechou, e a lista relida mostra a situação de agora, sem os botões que não valem.
   */
  aoRecusarReagendamento(erro: ErroAgendamento): void {
    this.agendamentosService.recarregar();

    // Em `texto`, não em `html`: a mensagem vem da API.
    void this.alerta.erro({
      titulo: 'Não foi possível alterar o agendamento',
      texto: [erro.mensagem, ...erro.detalhes].join(' '),
    });
  }

  async cancelar(agendamento: AgendamentoResponse): Promise<void> {
    // ⚠️ ANTES de qualquer alerta: o `<dialog>` aberto por `showModal()` fica na top layer, e o
    // SweetAlert abriria atrás dele, inerte.
    this.fecharDetalhes();

    if (this.cancelando()) return;

    this.cancelando.set(true);

    const motivo = await this.alerta.confirmarComTexto({
      titulo: 'Cancelar agendamento',
      html: `O atendimento de <b>${this.dataDe(agendamento)}</b>
             às <b>${this.horaDe(agendamento)}</b> será cancelado.<br>
             Não desmarcar com antecedência bloqueia novos agendamentos.`,
      rotuloCampo: 'Motivo do cancelamento',
      placeholderCampo: 'Descreva o motivo...',
      exigenciaCampo: 'Informe o motivo do cancelamento.',
      confirmar: 'Confirmar cancelamento',
      cancelar: 'Voltar',
    });

    if (motivo === null) {
      this.cancelando.set(false);
      return;
    }

    this.agendamentosService
      .cancelar(agendamento.desProtocoloSsv, motivo)
      .pipe(
        finalize(() => this.cancelando.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        // A lista já está sendo relida pelo serviço: o item volta como "Cancelado", sem o botão.
        next: (cancelado) =>
          void this.alerta.sucesso({
            titulo: 'Agendamento cancelado',
            html: `O protocolo <b>${cancelado.desProtocoloSsv}</b> foi cancelado.`,
          }),
        error: (erro: unknown) => this.aoFalharCancelamento(mapearErroAgendamento(erro, 'cancelar')),
      });
  }

  private aoFalharCancelamento(erro: ErroAgendamento): void {
    // O agendamento mudou sem a tela saber (o órgão concluiu, o horário passou, já foi
    // cancelado): a lista relida mostra a situação de agora, sem o botão de cancelar.
    if (erro.tipo === 'nao-encontrado' || erro.tipo === 'estado') this.agendamentosService.recarregar();

    // Em `texto`, não em `html`: a mensagem vem da API.
    void this.alerta.erro({
      titulo: 'Não foi possível cancelar',
      texto: [erro.mensagem, ...erro.detalhes].join(' '),
    });
  }
}
